import { ControllerSaveFunction } from "csi/middleware/common";
import { C8Node, cnl, ICache } from "csi/typings";
import { ILocaleProvider } from "designSystem/locale";
import { PullFromBOMResources, PullFromBOMStore } from "./PullFromBOMStore";
import { RestoreFromBOMStore } from "../Restore/RestoreFromBOMStore";
import FormData from "csi/action/FormData";
import { getSearch, runSearch } from "csi/service/dataStoreService";
import { createApparelBOMDSLineItemTree, createDeleteItemsTree } from "../Restore/RestoreFromBOMService";
import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import Writer from "csi/xml/Writer";
import { submitWriter } from "csi/service/writerService";
import xml from "csi/xml";

/** Create and initialize PullFromBOMStore */
export const createPullFromBOMStore = (
	actionId: string,
	cache: ICache,
	formData: FormData,
	bomRevisionURL: cnl,
	onClose: () => void,
	onSave: ControllerSaveFunction,
	locale: ILocaleProvider,
	resources: PullFromBOMResources,
): PullFromBOMStore => {
	return new PullFromBOMStore(actionId, onClose, onSave, cache, formData, locale, bomRevisionURL, resources);
};

/** Create and initialize RestoreFromBOMStore */
export const createRestoreFromSourceBOMStore = async (
	actionId: string,
	cache: ICache,
	formData: FormData,
	contextNode: C8Node,
	fromBOMCurrentRevisionUrl: cnl,
	onClose: () => void,
	onSave: ControllerSaveFunction,
	locale: ILocaleProvider,
	resources: PullFromBOMResources,
): Promise<RestoreFromBOMStore> => {
	const hostBOMRevisionUrl = contextNode.$URL;
	const hostRevisionItems = await fetchBOMRevisionItems(hostBOMRevisionUrl, cache);
	const sourceBOMitems = await fetchBOMRevisionItems(fromBOMCurrentRevisionUrl, cache);
	const updateItems = getUpdateItems(hostRevisionItems, sourceBOMitems);
	const updateItemsMap = await createApparelBOMDSLineItemTree(updateItems, "PullFromSourceBOM_StatusUpdate", locale, cache);
	const addItems = getAddItems(sourceBOMitems);
	const addItemsMap = await createApparelBOMDSLineItemTree(addItems, "PullFromSourceBOM_StatusAdd", locale, cache);
	const deleteItems = getDeleteItems(hostRevisionItems, sourceBOMitems);
	const deleteItemsMap = await createDeleteItemsTree(deleteItems, "PullFromSourceBOM_StatusDelete", locale, cache);
	return new RestoreFromBOMStore(actionId, cache, formData, contextNode, onClose, onSave, locale, resources, addItemsMap, deleteItemsMap, updateItemsMap, true, false);
};

const fetchBOMRevisionItems = async (bomRevisionUrl: cnl, cache: ICache): Promise<C8Node[]> => {
	const search = getSearch("DSLineItem.ApparelBOMRevision.StyleBOMDSLineItem", bomRevisionUrl);
	const response = await runSearch(search, cache);
	return response.getNodes();
};

/** 
 *  Get the items that are in both the host BOM and the source BOM.
 */
const getUpdateItems = (hostRevisionItems: C8Node[], sourceBOMitems: C8Node[]): C8Node[] => {
	const updateItems: C8Node[] = [];
	for (const hostItem of hostRevisionItems) {
		if (hostItem.$Type !== "PartMaterial") {
			updateItems.push(hostItem);
			continue;
		}
		if (hostItem.LinkedDSLineItemSysID) {
			const linkedItem = sourceBOMitems.find((item) => item.LinkedDSLineItemSysID === hostItem.LinkedDSLineItemSysID);
			if (linkedItem) {
				updateItems.push(hostItem);
			}
		}
	}
	return updateItems;
};

/** 
 *  Get the items that are in the source BOM but not in the host BOM.
 *  sourceBOMitems with empty LinkedDSLineItemSysID are considered as new items.
 */
const getAddItems = (sourceBOMitems: C8Node[]): C8Node[] => {
	return sourceBOMitems.filter((item) => !item.LinkedDSLineItemSysID);
};

/** 
 *  Get the items that are in the host BOM but not in the source BOM.
 */
const getDeleteItems = (hostRevisionItems: C8Node[], sourceBOMitems: C8Node[]): C8Node[] => {
	const deleteItems = [];
	for (const hostItem of hostRevisionItems) {
		if (hostItem.LinkedDSLineItemSysID) {
			const linkedItem = sourceBOMitems.find((item) => item.LinkedDSLineItemSysID === hostItem.LinkedDSLineItemSysID);
			if (!linkedItem) {
				deleteItems.push(hostItem);
			}
		}
	}
	return deleteItems;
};

/** Update Style BOMs. */
export async function pullFromSourceBOM(createRevision: boolean, restoreBOMRevisionURL: cnl, restoreAllPlacements: boolean, addPlacements: CheckboxTreeNode[], updatePlacements: CheckboxTreeNode[], tableSelectedNode: C8Node, deletePlacements: CheckboxTreeNode): Promise<JsonResponse> {
	const NotSelectedDeletedPlacements = deletePlacements.allLeafNodes.filter((node) => !node.state).
		map((node) => node.uniqueId);	
	const writer = new Writer();
	writer
		.callMethod("ProductSpec", "PullFromSourceBOM")
		.addParameter("URL", xml.ATTR_REF, restoreBOMRevisionURL)
		.addParameter("SourceBOM", xml.ATTR_REF, tableSelectedNode.$URL)
		.addParameter("AddPlacements", xml.ATTR_REFLIST, restoreAllPlacements ? addPlacements.map((node) => node.uniqueId) : [])
		.addParameter("UpdatePlacements", xml.ATTR_REFLIST, updatePlacements.map((node) => node.uniqueId))
		.addParameter("DeletePlacements", xml.ATTR_REFLIST, deletePlacements.selectedNodes.map((node) => node.uniqueId))
		.addParameter("NotSelectedDeletedPlacements", xml.ATTR_REFLIST, NotSelectedDeletedPlacements)
		.addParameter("CreateRevision", xml.ATTR_BOOLEAN, createRevision);

	return submitWriter(writer);
}